import React, { useCallback, useMemo, useState } from 'react';
import './AgencyStatsPage.scss';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { subDays } from 'date-fns';
import { PROJECTS } from '../../shared/routes/path';
import Title from '../../shared/components/title/Title';
import useAddon from '../../shared/hooks/global/UseAddon';
import { useAgencyStats } from '../../shared/hooks/agency-stats/UseAgencyStats';
import DateRangePicker from '../../shared/components/date-range-picker/DateRangePicker';
import { StandardPeriodType } from '../../shared/types/filters/StandardPeriodType';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import { getNextPayment } from '../../shared/services/pricing/PricingService';
import AgencyStatsTiles from './components/agency-stats-tiles/AgencyStatsTiles';
import AgencyStatsBarChart from './components/agency-stats-bar-chart/AgencyStatsBarChart';
import AgencyStatsPieChart from './components/agency-stats-pie-chart/AgencyStatsPieChart';
import AgencyStatsLineChart from './components/agency-stats-line-chart/AgencyStatsLineChart';
import AgencyStatsTable from './components/agency-stats-table/AgencyStatsTable';
import AgencyStatsInsightTiles from './components/agency-stats-insight-tiles/AgencyStatsInsightTiles';
import { enrichGroups, computeGroupDeltas, computeInsights } from './utils/agency-stats-utils';

const DEFAULT_PERIOD: StandardPeriodType = 'LAST_30_DAYS';

const getDefaultRange = (): Date[] => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const from = subDays(today, 29);
  from.setHours(0, 0, 0, 0);
  return [from, today];
};

const calculateRange = (period: string): Date[] | null => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  switch (period as StandardPeriodType) {
    case 'LAST_7_DAYS': {
      const from = subDays(today, 6);
      from.setHours(0, 0, 0, 0);
      return [from, today];
    }
    case 'LAST_14_DAYS': {
      const from = subDays(today, 13);
      from.setHours(0, 0, 0, 0);
      return [from, today];
    }
    case 'LAST_30_DAYS': {
      const from = subDays(today, 29);
      from.setHours(0, 0, 0, 0);
      return [from, today];
    }
    case 'LAST_3_MONTHS': {
      const from = subDays(today, 89);
      from.setHours(0, 0, 0, 0);
      return [from, today];
    }
    default:
      return null;
  }
};

const AgencyStatsPage: React.FC = () => {
  const { t } = useTranslation();
  const hasAgencyAddon = useAddon('AGENCY');
  const { accountResponse } = useAccount();

  const defaultRange = useMemo(() => getDefaultRange(), []);
  const [dateRange, setDateRange] = useState<Date[]>(defaultRange);
  const [selectedPeriod, setSelectedPeriod] = useState<string>(DEFAULT_PERIOD);

  const periodOptions = useMemo(
    () => [
      { label: t('last7Days'), value: 'LAST_7_DAYS' },
      { label: t('last14Days'), value: 'LAST_14_DAYS' },
      { label: t('last30Days'), value: 'LAST_30_DAYS' },
      { label: t('last3Months'), value: 'LAST_3_MONTHS' },
      { label: t('customPeriod'), value: 'CUSTOM' },
    ],
    [t]
  );

  const { data, isLoading } = useAgencyStats(dateRange[0], dateRange[1]);

  const { data: nextPayment } = useQuery({
    queryKey: ['next-payment'],
    queryFn: getNextPayment,
    staleTime: 5 * 60 * 1000,
  });

  const costPerPrompt = useMemo(() => {
    const monthlyPriceCents = nextPayment?.monthlyPrice;
    const maxMonitorCount = accountResponse?.maxMonitorCount;
    if (!monthlyPriceCents || !maxMonitorCount || maxMonitorCount === 0) return null;
    return (monthlyPriceCents / 100) / maxMonitorCount;
  }, [nextPayment, accountResponse]);

  const handleRangeChange = useCallback((range: Date[]) => {
    setDateRange(range);
  }, []);

  const handlePeriodChange = useCallback((period: string) => {
    setSelectedPeriod(period);
  }, []);

  const rangeDays = useMemo(() => {
    const diff = dateRange[1].getTime() - dateRange[0].getTime();
    return Math.max(1, Math.round(diff / (1000 * 60 * 60 * 24)));
  }, [dateRange]);

  const enrichedGroups = useMemo(() => {
    if (!data) return [];
    return enrichGroups(
      data.groups,
      data.previousGroups,
      data.totals,
      data.dailyGroupPrompts,
      rangeDays
    );
  }, [data, rangeDays]);

  const insights = useMemo(() => {
    if (!data) return { fastestGrowing: null, highestUsage: null, lowestActivity: null };
    const deltas = computeGroupDeltas(data.groups, data.previousGroups);
    return computeInsights(data.groups, deltas);
  }, [data]);

  const totalsDelta = useMemo(() => {
    if (!data) return { projectsDelta: 0, promptsDelta: 0 };
    return {
      projectsDelta: data.totals.totalProjects - data.previousTotals.totalProjects,
      promptsDelta: data.totals.totalPrompts - data.previousTotals.totalPrompts,
    };
  }, [data]);

  if (!hasAgencyAddon) {
    return <Navigate to={PROJECTS} replace />;
  }

  const groups = data?.groups ?? [];
  const totals = data?.totals;

  const groupCount = groups.filter((g) => g.groupId !== null).length;
  const projectCount = totals?.totalProjects ?? 0;
  const promptCount = totals?.totalPrompts ?? 0;

  return (
    <div className="page-content">
      <div className="page-box">
        <div className="page-box__filters" data-observe-sticky>
          <Title
            title={t('agencyStatsTitle')}
            children={
              <div className="filters">
                <DateRangePicker
                  value={dateRange}
                  onChange={handleRangeChange}
                  selectedPeriod={selectedPeriod}
                  onPeriodChange={handlePeriodChange}
                  periodOptions={periodOptions}
                  calculateRange={calculateRange}
                  defaultPeriod={DEFAULT_PERIOD}
                />
              </div>
            }
          />
        </div>

        <div className="page-box__stats">
          <AgencyStatsTiles
            groupCount={groupCount}
            projectCount={projectCount}
            promptCount={promptCount}
            projectsDelta={totalsDelta.projectsDelta}
            promptsDelta={totalsDelta.promptsDelta}
            isLoading={isLoading}
          />

          <div className="stats ">
            <AgencyStatsLineChart dailyGroupPrompts={data?.dailyGroupPrompts} groups={groups} />
            <AgencyStatsPieChart groups={groups} />
          </div>

          <div className="stats stats--full-width">
            <AgencyStatsBarChart groups={groups} />
          </div>

          <AgencyStatsInsightTiles insights={insights} />

          <AgencyStatsTable enrichedGroups={enrichedGroups} costPerPrompt={costPerPrompt} />
        </div>
      </div>
    </div>
  );
};

export default AgencyStatsPage;
